import React, { useState, useMemo } from "react";
import T from '../../../components/common/T';
import { useNavigate } from "react-router-dom";
import { useInventory } from "../../../context/InventoryContext";
import DataTable from "../../../components/common/DataTables.jsx";
import TableActionButtons from "../../../components/common/TableActionButtons";
import { TopPrimaryButton } from "../../../components/common/StandardButtons";
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from "../../../components/common/PageLayout";
import Price from "../../../components/common/Price";
import useModuleFilters from "../../../hooks/useModuleFilters";
import { 
  ArchiveBoxXMarkIcon, ExclamationTriangleIcon, CurrencyRupeeIcon,
  ArrowUturnLeftIcon, CurrencyDollarIcon
} from "@heroicons/react/24/outline";

export default function ScrapListPage() {
  const { scrapRequisitions = [], scrapList = [] } = useInventory();
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const {
    showFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
  } = useModuleFilters("scrap", {
    categories: [],
    statuses: [],
  });

  const actualData = scrapRequisitions.length > 0 ? scrapRequisitions : scrapList;

  // --- STATS DASHBOARD CALCULATION ---
  const stats = useMemo(() => {
    const totalLoss = actualData.reduce((acc, item) => acc + (item.cost || 0), 0);
    const doneCount = actualData.filter(i => 
        i.status === "RECEIVED" || i.status === "APPROVED" || i.status === "Done" || i.status === "SOLD" || i.status === "RECOVERED"
    ).length;
    
    return [
      { 
        label: "Total Scrapped", 
        value: actualData.length, 
        icon: ArchiveBoxXMarkIcon, 
        variant: "default", 
        isActive: statusFilter === "All" || !statusFilter,
        onClick: () => setStatusFilter("All") 
      },
      { 
        label: "Processed / Validated", 
        value: doneCount, 
        icon: ExclamationTriangleIcon, 
        variant: "green", 
        isActive: statusFilter === "Processed",
        onClick: () => setStatusFilter(s => s === "Processed" ? "All" : "Processed") 
      },
      { 
        label: "Total Loss Value", 
        value: <Price amount={totalLoss} />, 
        icon: CurrencyRupeeIcon, 
        variant: "red" 
      },
    ];
  }, [actualData, statusFilter]);

  // --- TABLE COLUMNS ---
  const columns = [
    { header: "Number", accessor: "id", sortable: true, render: (req) => <span className="font-semibold text-purple-700 dark:text-purple-400">{req.reference_number || req.id}</span> },
    { header: "Employee", accessor: "employee", sortable: true, render: (req) => <span className="font-medium text-gray-800 dark:text-white">{req.employee_name || req.employee || "-"}</span> },
    { header: "Department", accessor: "department", sortable: true, render: (req) => <span className="text-gray-600 dark:text-slate-300">{req.department || "-"}</span> },
    { header: "Date", accessor: "requisitionDate", sortable: true, render: (req) => req.requisition_date ? req.requisition_date.split('T')[0] : (req.requisitionDate || req.date || "-") },
    { header: "Company", accessor: "company", sortable: true, render: (req) => req.company || "-" },
    {
      header: "State",
      accessor: "status",
      align: "center",
      sortable: true,
      render: (req) => {
        const status = req.status || "NEW";
        let colorClass = 'text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700';
        if (status === 'RECEIVED' || status === 'Done') colorClass = 'text-green-700 dark:text-emerald-400 bg-green-50 dark:bg-emerald-950/40 border-green-200 dark:border-emerald-800/60';
        if (status === 'APPROVED') colorClass = 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800/60';
        if (status === 'SOLD') colorClass = 'text-yellow-700 dark:text-amber-400 bg-yellow-50 dark:bg-amber-950/40 border-yellow-200 dark:border-amber-800/60';
        if (status === 'RECOVERED') colorClass = 'text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-950/40 border-teal-200 dark:border-teal-800/60';

        return (
          <span className={`px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider border ${colorClass}`}>
            {status}
          </span>
        );
      }
    },
    { header: "Actions", align: "right", render: (req) => <TableActionButtons onView={() => navigate(`/admin/material/scrap/view/${req.id}`)} /> }
  ];

  const filteredScrap = useMemo(() => {
    let result = [...actualData];
    if (statusFilter && statusFilter !== "All") {
      if (statusFilter === "Processed") {
        result = result.filter(i => ["RECEIVED", "APPROVED", "Done", "SOLD", "RECOVERED"].includes(i.status));
      } else {
        result = result.filter(i => (i.status || "").toLowerCase() === statusFilter.toLowerCase());
      }
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(req => 
        (req.id?.toString() || "").toLowerCase().includes(s) ||
        (req.employee_name?.toString() || req.employee?.toString() || "").toLowerCase().includes(s) ||
        (req.department?.toString() || "").toLowerCase().includes(s)
      );
    }
    return result;
  }, [actualData, statusFilter, searchTerm]);

  return (
    <PageLayout>
      <PageHeader
        icon={ArchiveBoxXMarkIcon}
        iconColor="text-red-600"
        title="Scrap Orders"
        subtitle="Manage damaged and scrapped material"
        onRefresh={() => window.location.reload()}
      >
        <button 
          type="button" 
          onClick={() => {
            const q = selectedIds.length === 1 ? `?orderId=${selectedIds[0]}` : "";
            navigate(`/admin/material/scrap/reuse${q}`);
          }} 
          className="h-8 px-3 bg-white dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] rounded-lg text-[11px] font-bold uppercase hover:bg-gray-50 dark:hover:bg-slate-800 transition shadow-2xs flex items-center gap-1.5 text-green-700 dark:text-emerald-400 cursor-pointer"
        >
          <ArrowUturnLeftIcon className="w-3.5 h-3.5" /> <T>Recover</T>
        </button>
        
        <button 
          type="button" 
          onClick={() => {
            const q = selectedIds.length === 1 ? `?orderId=${selectedIds[0]}` : "";
            navigate(`/admin/material/scrap/sell${q}`);
          }} 
          className="h-8 px-3 bg-white dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] rounded-lg text-[11px] font-bold uppercase hover:bg-gray-50 dark:hover:bg-slate-800 transition shadow-2xs flex items-center gap-1.5 text-blue-700 dark:text-blue-400 cursor-pointer"
        >
          <CurrencyDollarIcon className="w-3.5 h-3.5" /> <T>Sell</T>
        </button>

        <TopPrimaryButton
          onClick={() => navigate("/admin/material/scrap/view/new")}
          label="New Order"
        />
      </PageHeader>
      
      {/* Stats */}
      <StatsGrid stats={stats} cols={3} />

      {/* Unified Toolbar */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search reference, employee, department..."
        tabs={['All', 'Processed', 'SOLD', 'RECOVERED']}
        activeTab={statusFilter}
        onTabChange={setStatusFilter}
        onToggleFilters={toggleFilters}
        showFilters={showFilter}
      />

      {/* Collapsible Filter Panel */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        onClear={() => { setStatusFilter("All"); setSearchTerm(""); }}
      >
        <div>
          <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Status</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            disabled={loadingFilters}
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="All"><T>All Statuses</T></option>
            {(filterOptions.statuses?.length > 0 ? filterOptions.statuses : ['Processed', 'SOLD', 'RECOVERED']).map(st => (
              <option key={st} value={st}>{st}</option>
            ))}
          </select>
        </div>
      </FilterPanel>

      {/* Table Card */}
      <TableCard>
        <DataTable
          data={filteredScrap}
          columns={columns}
          emptyMessage="No scrap records found."
          selectable={true}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </TableCard>
    </PageLayout>
  );
}